<script setup lang="ts">
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { InventTypeField } from '@/entities/Inventory/TypeField'
import { VIcons } from '@/shared/ui'
import { computed, type PropType } from 'vue'
import InventObjectTypesFieldFlag from './InventObjectTypesFieldFlag.vue'

const MANDATORY_EXCLUDED_TYPES = new Set(['computed', 'interator_simple', 'boolean'])

const field = defineModel<ObjectInventoryField>('field', { required: true })

const props = defineProps({
  typeItems: {
    type: Array as PropType<{ type?: string; title: string; value?: string }[]>,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  parentDefault: {
    type: [String, Array] as PropType<string | string[]>,
    default: undefined,
  },
})

const emit = defineEmits<{
  editSettings: [field: ObjectInventoryField]
  delete: [id: string]
}>()

/** Числовой id — признак сохранённого в БД поля (у новых полей случайный строковый ключ) */
const isPersisted = computed(() => /^\d+$/.test(field.value.id))

/** Сброс показываем только для унаследованных полей с изменённым значением по умолчанию */
const showReset = computed(
  () => props.readonly && props.parentDefault != field.value.default_value,
)

/** Сброс значения по умолчанию к родительскому значению */
const resetHandler = () => {
  field.value.default_value = String(props.parentDefault ?? '')
}
</script>

<template>
  <div class="invent-object-types-fields__card">
    <div class="invent-object-types-fields__card-flags">
      <div class="invent-object-types-fields__card-id">
        <template v-if="isPersisted">
          <VIcons type="numbers" />
          {{ field.id }}
        </template>

        <template v-else>
          <VChip size="x-small" variant="tonal" label>
            {{ $t('itf.field_new') }}
          </VChip>
        </template>
      </div>

      <div class="invent-object-types-fields__card-flags-center">
        <InventObjectTypesFieldFlag
          v-model="field.is_filtered"
          icon="filter_alt"
          :tooltip="$t('itf.show_in_filter')"
          :readonly="readonly"
        />

        <InventObjectTypesFieldFlag
          v-model="field.is_table"
          icon="table_eye"
          :tooltip="$t('itf.show_in_table')"
          :readonly="readonly"
        />

        <InventObjectTypesFieldFlag
          v-model="field.is_global_search"
          icon="search"
          :tooltip="$t('itf.is_global_search')"
          :readonly="readonly"
        />
      </div>

      <div class="invent-object-types-fields__card-flags-right">
        <InventObjectTypesFieldFlag
          v-model="field.is_system"
          icon="visibility_off"
          :tooltip="$t('itf.is_system_field')"
          :readonly="readonly"
        />
      </div>
    </div>

    <div class="invent-object-types-fields__card-row">
      <InventObjectTypesFieldFlag
        v-if="!MANDATORY_EXCLUDED_TYPES.has(String(field.type ?? ''))"
        v-model="field.mandatory"
        icon="asterisk"
        :tooltip="$t('itf.required')"
      />

      <VTextField
        :label="$t('itf.name_label')"
        :readonly="readonly"
        v-model="field.name"
        hide-details
      />

      <div class="invent-object-types-fields__card-actions">
        <VBtn
          v-if="!readonly"
          class="invent-object-types-fields__square-btn"
          variant="tonal"
          color="default"
          @click="emit('editSettings', field)"
        >
          <VIcons type="tune" />
        </VBtn>

        <VBtn
          class="invent-object-types-fields__square-btn"
          variant="tonal"
          color="error"
          :disabled="readonly"
          @click="emit('delete', field.id)"
        >
          <VIcons type="delete" />
        </VBtn>
      </div>
    </div>

    <div class="invent-object-types-fields__card-row">
      <VSelect
        class="invent-object-types-fields__card-type"
        :label="$t('itf.type_field')"
        :items="typeItems"
        :readonly="readonly"
        v-model="field.type"
        hide-details
      />

      <div class="invent-object-types-fields__default-wrap">
        <InventTypeField
          :label="$t('itf.default_value')"
          :field-data="field"
          v-bind="{ ...field, mandatory: false }"
          hide-details
          v-model="field.default_value"
        />

        <VBtn
          class="invent-object-types-fields__square-btn"
          v-if="showReset"
          color="default"
          variant="tonal"
          @click="resetHandler"
        >
          <VIcons type="close" />
        </VBtn>
      </div>
    </div>
  </div>
</template>
